import {useSelector} from "react-redux";
import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useApiGet} from "../hooks/useApiHook";
import Checkers from "../helpers/class/Checkers";
import {BODY_END_WIDGETS_ID, fetchBuilderSettingByKey, uniqueElfsightSections} from "../helpers/MethodHelper";

const BodyEndSectionsWrapper = () => {

    const pageId = useSelector(state => state['builderSettingsData']['settings']['page_id'])

    const sections = useApiGet(`/api/lodge/frontend/get/app-sections/${pageId}`, 'position=end_body&type=HTML', false)

    const loadedRef = useRef(false)

    // These end_body sections are also part of the page's html_sections, whose Elfsight widgets
    // <BottomSections> already renders (server side, so they are in the DOM by now). Rendering them here too
    // would show every widget twice (closing the popup reveals a second one). Computed once per response,
    // so this component's own widgets are not mistaken for duplicates on later renders.
    const bodyEndSections = useMemo(() => {
        if (sections.status != 200 || !Checkers.isValidArray(sections.data?.app_sections)) return []
        return uniqueElfsightSections(sections.data.app_sections).filter((section) => {
            const match = typeof section.app_section_content === 'string' ? section.app_section_content.match(/elfsight-app-([\w-]+)/) : null
            return match == null || typeof document === 'undefined' || document.querySelector('.elfsight-app-' + match[1]) == null
        })
    }, [sections.status, sections.data])

    const showAccessYes = useSelector(
        state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_accessyes_enable') == 1 ? true : false
    );


    const accessYesIdentifier = useSelector(
        state => fetchBuilderSettingByKey(state['builderSettingsData'], 'builder_general_accessyes_identifier')
    );

    useEffect(() => {
        // μόνο client-side
        if (typeof window === "undefined") return;

        // feature flag off
        if (!showAccessYes) return;

        // αν δεν έχεις identifier, δεν το φορτώνουμε
        if (!accessYesIdentifier) return;

        // avoid double inject
        if (loadedRef.current) return;

        // avoid duplicate if script already exists (σε route changes)
        const existing = document.querySelector(
            'script[data-cookieyes-a11y="1"]'
        ) as HTMLScriptElement | null;
        if (existing) {
            loadedRef.current = true;
            return;
        }

        loadedRef.current = true;

        // config πριν το load
        (window as any)._cyA11yConfig = {
            iconId: "default",
            position: { mobile: "bottom-right", desktop: "bottom-right" },
            language: { default: "en", selected: [] },
        };

        const script = document.createElement("script");
        script.async = true;
        script.src = `https://cdn-cookieyes.com/widgets/accessibility.js?id=${encodeURIComponent(
            accessYesIdentifier
        )}`;

        script.setAttribute("data-cookieyes-a11y", "1");

        document.body.appendChild(script);

        // optional: cleanup αν θες να αφαιρείται σε unmount
        // return () => { script.remove() }
    }, [showAccessYes, accessYesIdentifier]);

    // Elfsight widgets go to the mount point above the footer (BottomSections), so an inline widget such as
    // the reviews is not shown under the footer; popups and chat float, so their position does not matter.
    // Other end_body HTML stays here, at the end of the page. Without the mount point (pages without
    // BottomSections) everything is rendered here as before.
    // BottomSections loads lazily and can remount (skeleton -> content), replacing the mount point, so the
    // current element is tracked instead of read once.
    const [widgetsTarget, setWidgetsTarget] = useState<HTMLElement | null>(null)
    const [noWidgetsTarget, setNoWidgetsTarget] = useState(false)

    useEffect(() => {
        const update = () => {
            const element = document.getElementById(BODY_END_WIDGETS_ID)
            setWidgetsTarget((current) => current === element ? current : element)
        }
        update()
        const observer = new MutationObserver(update)
        observer.observe(document.body, {childList: true, subtree: true})
        // pages without BottomSections never get the mount point: after a while render the widgets here instead
        const fallbackTimer = window.setTimeout(() => setNoWidgetsTarget(document.getElementById(BODY_END_WIDGETS_ID) == null), 5000)
        return () => {
            observer.disconnect()
            window.clearTimeout(fallbackTimer)
        }
    }, [])
    const isElfsight = (section) => typeof section.app_section_content === 'string' && section.app_section_content.includes('elfsight-app-')
    // Elfsight widgets are created once, where they will stay: in the mount point, or here when the page has
    // none. Rendering them here first and moving them later leaves an empty widget (Elfsight initialises a
    // widget id once), so until the mount point is found they wait.
    const elfsightSections = widgetsTarget != null ? bodyEndSections.filter(isElfsight) : []
    const inPlaceSections = widgetsTarget == null && noWidgetsTarget
        ? bodyEndSections
        : bodyEndSections.filter((section) => !isElfsight(section))

    return (
        <>
            {
                inPlaceSections.map( (section, index) => (
                    <div key={'end-body-' + index} dangerouslySetInnerHTML={{__html: section.app_section_content}} />
                ))
            }
            {
                elfsightSections.length > 0 && widgetsTarget != null &&
                createPortal(
                    elfsightSections.map( (section, index) => (
                        <div key={'end-body-widget-' + index} dangerouslySetInnerHTML={{__html: section.app_section_content}} />
                    )),
                    widgetsTarget
                )
            }
        </>
    )
}

export default BodyEndSectionsWrapper